import { Bookmark } from "@novorender/data-js-api";
import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";

import { type RootState } from "app";
import { initScene } from "features/render";
import { AsyncState, AsyncStatus, DeepMutable } from "types/misc";

import { applyGroupStatusToBookmark } from "./utils";

export enum BookmarkAccess {
    Public,
    Personal,
}

export type ExtendedBookmark = Omit<Bookmark, "publicIdMap" | "version"> & { access: BookmarkAccess };

const initialState = {
    initStatus: AsyncStatus.Initial,
    saveStatus: { status: AsyncStatus.Initial } as AsyncState<null | string>,
    bookmarks: [] as ExtendedBookmark[],
    supportsV2: false,
    filters: {
        title: "",
        measurements: false,
        clipping: false,
        groups: false,
        personal: false,
        public: false,
    },
    expandedCollections: [] as string[],
    confirmLargeBookmark: (_count: number) => Promise.resolve(true),
};

type State = typeof initialState;
export type Filters = State["filters"];

export const bookmarksSlice = createSlice({
    name: "bookmarks",
    initialState: initialState,
    reducers: {
        setInitStatus: (state, action: PayloadAction<State["initStatus"]>) => {
            state.initStatus = action.payload;
        },
        setSaveStatus: (state, action: PayloadAction<State["saveStatus"]>) => {
            state.saveStatus = action.payload;
        },
        setFilters: (state, action: PayloadAction<Partial<State["filters"]>>) => {
            state.filters = {
                ...state.filters,
                ...action.payload,
            };
        },
        toggleFilter: (state, action: PayloadAction<Exclude<keyof State["filters"], "title">>) => {
            state.filters[action.payload] = !state.filters[action.payload];
        },
        setBookmarks: (state, action: PayloadAction<{ bookmarks: State["bookmarks"] }>) => {
            state.bookmarks = action.payload.bookmarks as DeepMutable<State["bookmarks"]>;
        },
        resetState: () => {
            return initialState;
        },
        expandCollection: (state, action: PayloadAction<string>) => {
            state.expandedCollections = state.expandedCollections.concat(action.payload);
        },
        closeCollection: (state, action: PayloadAction<string>) => {
            state.expandedCollections = state.expandedCollections.filter((collection) => collection !== action.payload);
        },
        renameExpandedCollection: (state, { payload: { from, to } }: PayloadAction<{ from: string; to: string }>) => {
            state.expandedCollections = state.expandedCollections.map((collection) =>
                collection.startsWith(from) ? collection.replace(from, to) : collection,
            );
        },
        setConfirmLargeBookmark: (state, action: PayloadAction<State["confirmLargeBookmark"]>) => {
            state.confirmLargeBookmark = action.payload;
        },
        setGroupStatusInAllPublicBookmarks: (
            state,
            { payload: { id, status } }: PayloadAction<{ id: string; status: "hidden" | "frozen" }>,
        ) => {
            state.bookmarks = state.bookmarks.map((bm) =>
                bm.access === BookmarkAccess.Public ? applyGroupStatusToBookmark(bm, id, status) : bm,
            ) as DeepMutable<State["bookmarks"]>;
        },
    },
    extraReducers: (builder) =>
        builder.addCase(initScene, (state, action) => {
            const [major, minor] = (action.payload.pipelineConfig?.lastFullBuildVersion ?? "0")
                .split(".")
                .map((segment) => Number(segment));
            state.supportsV2 = major > 4 || (major === 4 && minor >= 4);
        }),
});

export const selectSaveStatus = (state: RootState) => state.bookmarks.saveStatus;
export const selectBookmarkFilters = (state: RootState) => state.bookmarks.filters;
export const selectExpandedCollections = (state: RootState) => state.bookmarks.expandedCollections;
export const selectBookmarks = (state: RootState) => state.bookmarks.bookmarks;
export const selectBookmarksStatus = (state: RootState) => state.bookmarks.initStatus;
export const selectIsV2BookmarkSupported = (state: RootState) => state.bookmarks.supportsV2;
export const selectConfirmLargeBookmark = (state: RootState) => state.bookmarks.confirmLargeBookmark;

export const selectIsCollectionExpanded = createSelector(
    [selectExpandedCollections, (_state, collection: string) => collection],
    (collections, collection) => collections.includes(collection),
);

const { actions, reducer } = bookmarksSlice;
export { actions as bookmarksActions, reducer as bookmarksReducer };
